// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyAdmin } from "@/types";
import Icon from "@/UI/atoms/Icon";
import AdminCard from "@/UI/cards/AdminCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import AdminFormPopup from "@/UI/popups/AdminFormPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import PasswordPopup from "@/UI/popups/PasswordPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "all" | "supers" | "suspended";

/** What each scope asks the endpoint for. A tab is a different list, not a narrowing of one. */
const TAB_QUERY: Record<TyTab, Record<string, string>> = {
    all: {},
    supers: { isSuper: "true" },
    suspended: { isActive: "false" },
};

/**
 * Lozicore's own team, and the whole of the admins model: list, create, edit, issue a password, and
 * suspend. **There is no delete** — an admin record is the actor on everything they ever touched, so
 * access is revoked with `isActive` and the row stays.
 *
 * Super-only, and `RoleRoute` is what enforces it; this screen never asks. There is nothing to
 * configure either: an admin either is super or is not, and the only thing that turns on it is
 * managing this very list.
 *
 * The one rule worth knowing before reading the actions: **the API refuses anything self-destructive**
 * — demoting yourself, deactivating yourself, resetting your own password — and it refuses demoting or
 * deactivating the last active super admin, because that would leave this screen reachable by nobody.
 * The UI does not offer either, rather than offering a button that exists to be refused.
 */
export default function AdminsScreen() {
    const t = useTranslation();
    const me = useAuth((state) => state.admin);
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "all";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    /** Who the password in flight belongs to. The response carries the secret, never the name. */
    const [issuedFor, setIssuedFor] = useState("");

    const admins = useFetch<{ admins: TyAdmin[]; total: number }>("/admins", {}, false);
    /** Row actions share one call: only one can be in flight, and each sets its own path. */
    const action = useFetch("/admins", {}, false);

    const load = () => {
        admins.invoke({ query: { ...TAB_QUERY[tab], search: search.trim() || undefined, page, limit: pageSize } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = admins.data?.admins ?? [];
    /**
     * Which row the server would refuse to demote or deactivate. Computed from the page in hand, so it
     * is only right while the whole team fits on one page — which it does, and the API is the one that
     * actually enforces it. The chip is a courtesy, not the rule.
     */
    const activeSupers = rows.filter((admin) => admin.isSuper && admin.isActive);
    const lastSuperId = activeSupers.length === 1 ? activeSupers[0]._id : null;

    const tabs: TyBarTab[] = (Object.keys(TAB_QUERY) as TyTab[]).map((key) => ({ key, label: t.admins.tabs[key] }));

    const openForm = (admin: TyAdmin | null) =>
        addComp({
            name: "admin-form",
            Component: AdminFormPopup,
            anim: "scale",
            details: { admin, onDone: load },
        });

    const openActions = (admin: TyAdmin, anchor: DOMRect) => {
        const isLastSuper = admin._id === lastSuperId;

        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openForm(admin) },
            {
                key: "password",
                label: t.password.issue,
                icon: "key",
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.password.issue,
                            message: t.password.confirmHint.replace("{name}", admin.name),
                            confirmLabel: t.password.issue,
                            danger: false,
                            onConfirm: () => {
                                setIssuedFor(admin.name);
                                action.invoke({ method: "POST", path: `/admins/${admin._id}/password` });
                            },
                        },
                    }),
            },
            // The one state the list may never reach, so the action is not offered on that row.
            ...(isLastSuper
                ? []
                : [
                      {
                          key: "active",
                          label: admin.isActive ? t.admins.deactivate : t.admins.activate,
                          icon: "block" as const,
                          danger: admin.isActive,
                          onSelect: () =>
                              addComp({
                                  name: "confirm",
                                  Component: ConfirmPopup,
                                  anim: "scale",
                                  details: {
                                      title: admin.isActive ? t.admins.deactivate : t.admins.activate,
                                      message: (admin.isActive ? t.admins.confirmDeactivate : t.admins.confirmActivate).replace("{name}", admin.name),
                                      confirmLabel: admin.isActive ? t.admins.deactivate : t.admins.activate,
                                      danger: admin.isActive,
                                      onConfirm: () =>
                                          action.invoke({ method: "PATCH", path: `/admins/${admin._id}`, data: { isActive: !admin.isActive } }),
                                  },
                              }),
                      },
                  ]),
        ];

        addComp({
            name: "admin-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /** A password only ever exists in the response that created it, so it is shown the moment it lands. */
    useEffect(() => {
        const password = (action.data as { password?: string } | null)?.password;
        if (!action.succeededAt || !password) return;
        addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name: issuedFor, password } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    return (
        <section className="list-screen">
            <ListPanel title={t.admins.title} subtitle={t.admins.subtitle.replace("{count}", String(admins.data?.total ?? 0))}>
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "all" ? null : key })}
                    addLabel={t.admins.addAdmin}
                    onAdd={() => openForm(null)}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.admins.searchPlaceholder}
                            onEnter={() => {
                                setIsFiltering(false);
                                apply({ search: draftSearch.trim() || null });
                            }}
                        />
                    }
                />
            </ListPanel>

            <p className="admins-screen__note">
                <Icon name="crown" size={16} />
                <span>{t.admins.superOnly}</span>
            </p>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}
            {admins.loading && <p className="screen-state">{t.common.loading}</p>}
            {admins.error && <p className="screen-state screen-state--error">{admins.error}</p>}
            {!admins.loading && !admins.error && !rows.length && <p className="screen-state">{t.admins.empty}</p>}

            <div className="list-screen__rows">
                {rows.map((admin) => (
                    <AdminCard
                        key={admin._id}
                        admin={admin}
                        isMe={admin._id === me?._id}
                        isLastSuper={admin._id === lastSuperId}
                        onEdit={() => openForm(admin)}
                        onActions={(anchor) => openActions(admin, anchor)}
                    />
                ))}
            </div>

            <Paginator
                page={page}
                total={admins.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
